import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { mediaRecommendationService } from '@/services/mediaRecommendation.service';
import type {
  MediaRecommendation,
  MediaComment,
  MediaFilters,
  CreateMediaRecommendationInput,
  UpdateMediaRecommendationInput,
  PaginatedMediaResponse,
  PaginatedMediaCommentsResponse,
  MediaPaginationMeta,
} from '@/types/mediaRecommendation.types';
import { ApiError } from '@/services/api';

// Media Recommendation state interface
interface MediaRecommendationState {
  recommendations: MediaRecommendation[];
  currentMedia: MediaRecommendation | null;
  comments: MediaComment[];
  meta: MediaPaginationMeta | null;
  commentsMeta: MediaPaginationMeta | null;
  filters: MediaFilters;
  isLoading: boolean;
  isLoadingMedia: boolean;
  isLoadingComments: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isRating: boolean;
  isAddingComment: boolean;
  error: string | null;
}

// Initial state
const initialState: MediaRecommendationState = {
  recommendations: [],
  currentMedia: null,
  comments: [],
  meta: null,
  commentsMeta: null,
  filters: {
    page: 1,
    limit: 12,
    sortBy: 'date',
    sortOrder: 'desc',
  },
  isLoading: false,
  isLoadingMedia: false,
  isLoadingComments: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isRating: false,
  isAddingComment: false,
  error: null,
};

// ==============================================
// Async Thunks - Media Operations
// ==============================================

export const fetchRecommendations = createAsyncThunk<
  PaginatedMediaResponse,
  MediaFilters | undefined,
  { rejectValue: string }
>('mediaRecommendation/fetchRecommendations', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { mediaRecommendation: MediaRecommendationState };
    const mergedFilters = { ...state.mediaRecommendation.filters, ...filters };
    return await mediaRecommendationService.getRecommendations(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Empfehlungen');
  }
});

export const fetchRecommendationById = createAsyncThunk<
  MediaRecommendation,
  string,
  { rejectValue: string }
>('mediaRecommendation/fetchById', async (id, { rejectWithValue }) => {
  try {
    return await mediaRecommendationService.getRecommendationById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Empfehlung');
  }
});

export const createRecommendation = createAsyncThunk<
  MediaRecommendation,
  CreateMediaRecommendationInput,
  { rejectValue: string }
>('mediaRecommendation/create', async (data, { rejectWithValue }) => {
  try {
    return await mediaRecommendationService.createRecommendation(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen der Empfehlung');
  }
});

export const updateRecommendation = createAsyncThunk<
  MediaRecommendation,
  { id: string; data: UpdateMediaRecommendationInput },
  { rejectValue: string }
>('mediaRecommendation/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await mediaRecommendationService.updateRecommendation(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren der Empfehlung');
  }
});

export const deleteRecommendation = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('mediaRecommendation/delete', async (id, { rejectWithValue }) => {
  try {
    await mediaRecommendationService.deleteRecommendation(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen der Empfehlung');
  }
});

// ==============================================
// Async Thunks - Rating Operations
// ==============================================

export const rateMedia = createAsyncThunk<
  MediaRecommendation,
  { id: string; rating: number },
  { rejectValue: string }
>('mediaRecommendation/rate', async ({ id, rating }, { rejectWithValue }) => {
  try {
    return await mediaRecommendationService.rateMedia(id, { rating });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Bewerten');
  }
});

export const removeMediaRating = createAsyncThunk<
  MediaRecommendation,
  string,
  { rejectValue: string }
>('mediaRecommendation/removeRating', async (id, { rejectWithValue }) => {
  try {
    return await mediaRecommendationService.removeRating(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Entfernen der Bewertung');
  }
});

// ==============================================
// Async Thunks - Comment Operations
// ==============================================

export const fetchMediaComments = createAsyncThunk<
  PaginatedMediaCommentsResponse,
  { mediaId: string; page?: number; limit?: number },
  { rejectValue: string }
>('mediaRecommendation/fetchComments', async ({ mediaId, page = 1, limit = 20 }, { rejectWithValue }) => {
  try {
    return await mediaRecommendationService.getComments(mediaId, page, limit);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Kommentare');
  }
});

export const addMediaComment = createAsyncThunk<
  MediaComment,
  { mediaId: string; comment: string },
  { rejectValue: string }
>('mediaRecommendation/addComment', async ({ mediaId, comment }, { rejectWithValue }) => {
  try {
    return await mediaRecommendationService.addComment(mediaId, { comment });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Hinzufügen des Kommentars');
  }
});

export const deleteMediaComment = createAsyncThunk<
  { mediaId: string; commentId: string },
  { mediaId: string; commentId: string },
  { rejectValue: string }
>('mediaRecommendation/deleteComment', async ({ mediaId, commentId }, { rejectWithValue }) => {
  try {
    await mediaRecommendationService.deleteComment(mediaId, commentId);
    return { mediaId, commentId };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Löschen des Kommentars');
  }
});

// ==============================================
// Slice
// ==============================================

const mediaRecommendationSlice = createSlice({
  name: 'mediaRecommendation',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentMedia: (state) => {
      state.currentMedia = null;
      state.comments = [];
      state.commentsMeta = null;
    },
    setFilters: (state, action: PayloadAction<Partial<MediaFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    // Fetch recommendations
    builder
      .addCase(fetchRecommendations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recommendations = action.payload.recommendations;
        state.meta = action.payload.meta;
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Fetch single recommendation
    builder
      .addCase(fetchRecommendationById.pending, (state) => {
        state.isLoadingMedia = true;
        state.error = null;
      })
      .addCase(fetchRecommendationById.fulfilled, (state, action) => {
        state.isLoadingMedia = false;
        state.currentMedia = action.payload;
      })
      .addCase(fetchRecommendationById.rejected, (state, action) => {
        state.isLoadingMedia = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Create recommendation
    builder
      .addCase(createRecommendation.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createRecommendation.fulfilled, (state, action) => {
        state.isCreating = false;
        state.recommendations.unshift(action.payload);
        if (state.meta) {
          state.meta.total++;
        }
      })
      .addCase(createRecommendation.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Update recommendation
    builder
      .addCase(updateRecommendation.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateRecommendation.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.recommendations.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.recommendations[index] = action.payload;
        }
        if (state.currentMedia?.id === action.payload.id) {
          state.currentMedia = action.payload;
        }
      })
      .addCase(updateRecommendation.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Delete recommendation
    builder
      .addCase(deleteRecommendation.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteRecommendation.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.recommendations = state.recommendations.filter(r => r.id !== action.payload);
        if (state.currentMedia?.id === action.payload) {
          state.currentMedia = null;
        }
        if (state.meta) {
          state.meta.total--;
        }
      })
      .addCase(deleteRecommendation.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Rate media
    builder
      .addCase(rateMedia.pending, (state) => {
        state.isRating = true;
        state.error = null;
      })
      .addCase(rateMedia.fulfilled, (state, action) => {
        state.isRating = false;
        const index = state.recommendations.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.recommendations[index] = action.payload;
        }
        if (state.currentMedia?.id === action.payload.id) {
          state.currentMedia = action.payload;
        }
      })
      .addCase(rateMedia.rejected, (state, action) => {
        state.isRating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Remove rating
    builder
      .addCase(removeMediaRating.pending, (state) => {
        state.isRating = true;
        state.error = null;
      })
      .addCase(removeMediaRating.fulfilled, (state, action) => {
        state.isRating = false;
        const index = state.recommendations.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.recommendations[index] = action.payload;
        }
        if (state.currentMedia?.id === action.payload.id) {
          state.currentMedia = action.payload;
        }
      })
      .addCase(removeMediaRating.rejected, (state, action) => {
        state.isRating = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Fetch comments
    builder
      .addCase(fetchMediaComments.pending, (state) => {
        state.isLoadingComments = true;
        state.error = null;
      })
      .addCase(fetchMediaComments.fulfilled, (state, action) => {
        state.isLoadingComments = false;
        state.comments = action.payload.comments;
        state.commentsMeta = action.payload.meta;
      })
      .addCase(fetchMediaComments.rejected, (state, action) => {
        state.isLoadingComments = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Add comment
    builder
      .addCase(addMediaComment.pending, (state) => {
        state.isAddingComment = true;
        state.error = null;
      })
      .addCase(addMediaComment.fulfilled, (state, action) => {
        state.isAddingComment = false;
        state.comments.unshift(action.payload);
        if (state.commentsMeta) {
          state.commentsMeta.total++;
        }
        if (state.currentMedia) {
          state.currentMedia._count.comments++;
        }
      })
      .addCase(addMediaComment.rejected, (state, action) => {
        state.isAddingComment = false;
        state.error = action.payload ?? 'Unbekannter Fehler';
      });

    // Delete comment
    builder
      .addCase(deleteMediaComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter(c => c.id !== action.payload.commentId);
        if (state.commentsMeta) {
          state.commentsMeta.total--;
        }
        if (state.currentMedia) {
          state.currentMedia._count.comments--;
        }
      });
  },
});

// Export actions
export const {
  clearError,
  clearCurrentMedia,
  setFilters,
  resetFilters,
} = mediaRecommendationSlice.actions;

// Export selectors
export const selectRecommendations = (state: { mediaRecommendation: MediaRecommendationState }) =>
  state.mediaRecommendation.recommendations;
export const selectCurrentMedia = (state: { mediaRecommendation: MediaRecommendationState }) =>
  state.mediaRecommendation.currentMedia;
export const selectMediaComments = (state: { mediaRecommendation: MediaRecommendationState }) =>
  state.mediaRecommendation.comments;
export const selectMediaMeta = (state: { mediaRecommendation: MediaRecommendationState }) =>
  state.mediaRecommendation.meta;
export const selectMediaFilters = (state: { mediaRecommendation: MediaRecommendationState }) =>
  state.mediaRecommendation.filters;
export const selectMediaLoading = (state: { mediaRecommendation: MediaRecommendationState }) => ({
  isLoading: state.mediaRecommendation.isLoading,
  isLoadingMedia: state.mediaRecommendation.isLoadingMedia,
  isLoadingComments: state.mediaRecommendation.isLoadingComments,
  isCreating: state.mediaRecommendation.isCreating,
  isUpdating: state.mediaRecommendation.isUpdating,
  isDeleting: state.mediaRecommendation.isDeleting,
  isRating: state.mediaRecommendation.isRating,
  isAddingComment: state.mediaRecommendation.isAddingComment,
});
export const selectMediaError = (state: { mediaRecommendation: MediaRecommendationState }) =>
  state.mediaRecommendation.error;

export default mediaRecommendationSlice.reducer;
